import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import moment from 'moment';

import {
  createEdicao,
  deleteEdicao,
  listCategoriesAdmin,
  listDificuldades,
  listEdicoes,
  updateEdicao,
  type CategoryAdmin,
  type Dificuldade,
  type EdicaoAdmin,
} from '../../services/admin';
import { EDITION_KIND_LABEL, editionKindOf } from './academy';
import { apiErrorMessage } from './desafios';
import { toPayload, type EdicaoFormValues } from './edicao-form';

const { TextArea } = Input;

const LEGACY_DESCRIPTION_HELP =
  'Descrição antiga com mais de 160 caracteres. Ao editar, reduza para até 160.';

/** Descrição legada em branco ou literal 'null' conta como vazia. */
const cleanDescription = (v: string | null): string | undefined => {
  const t = v?.trim();
  return !t || t.toLowerCase() === 'null' ? undefined : v ?? undefined;
};

const saveErrorMessage = (err: unknown): string => {
  const apiMsg = apiErrorMessage(err);
  if (apiMsg === 'description_too_long') return 'A descrição passou de 160 caracteres.';
  if (apiMsg === 'unknown_category') {
    return 'Uma das categorias não existe mais. Recarregue a página.';
  }
  const legacy = (err as { response?: { data?: { msg?: unknown } } })?.response?.data?.msg;
  return apiMsg ?? (typeof legacy === 'string' ? legacy : undefined) ?? 'Falha ao salvar a edição.';
};

const fmtDate = (iso: string | null): string => (iso ? moment(iso).format('DD/MM/YYYY') : '—');
// `valorAVista` trafega em CENTAVOS (convenção da API — o app faz o mesmo);
// aqui exibimos/editamos em reais, então dividimos/multiplicamos por 100.
const fmtPreco = (v: string | null): string =>
  v == null ? '—' : `R$ ${(Number(v) / 100).toFixed(2).replace('.', ',')}`;

export const EdicoesCatalog: React.FC = () => {
  const [edicoes, setEdicoes] = useState<EdicaoAdmin[]>([]);
  const [dificuldades, setDificuldades] = useState<Dificuldade[]>([]);
  const [categories, setCategories] = useState<CategoryAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<EdicaoAdmin | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<EdicaoFormValues>();
  const descricaoValue = Form.useWatch('descricao', form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEdicoes(await listEdicoes());
    } catch {
      message.error('Falha ao carregar as edições.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Retorna a lista fresca (ou null se falhar, mantendo a anterior).
  const refreshCategories = useCallback(async (): Promise<CategoryAdmin[] | null> => {
    try {
      const fresh = await listCategoriesAdmin();
      setCategories(fresh);
      return fresh;
    } catch {
      message.error('Falha ao carregar as categorias.');
      return null;
    }
  }, []);

  useEffect(() => {
    load();
    listDificuldades()
      .then(setDificuldades)
      .catch(() => message.error('Falha ao carregar as dificuldades.'));
    refreshCategories();
  }, [load, refreshCategories]);

  const openCreate = () => {
    refreshCategories();
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ aVenda: false, liberadaGratis: false, kind: 'DEFAULT' });
    setModalOpen(true);
  };

  const openEdit = async (e: EdicaoAdmin) => {
    // As abas do antd ficam montadas: relê as categorias para não enviar ids excluídos.
    const fresh = await refreshCategories();
    const known = fresh ?? categories;
    const categoryIds = e.categoryIds.filter((id) => known.some((c) => c.id === id));
    setEditing(e);
    form.setFieldsValue({
      nome: e.nome ?? '',
      subtitulo: e.subtitulo ?? undefined,
      descricao: cleanDescription(e.descricao),
      dataPublicacao: e.dataPublicacao ? moment(e.dataPublicacao) : null,
      valorAVista: e.valorAVista != null ? Number(e.valorAVista) / 100 : null,
      aVenda: !!e.aVenda,
      liberadaGratis: !!e.liberadaGratis,
      torneioInicio: e.torneioInicio ? moment(e.torneioInicio) : null,
      torneioFim: e.torneioFim ? moment(e.torneioFim) : null,
      dificuldadesId: e.dificuldadesId ?? undefined,
      author: e.author ?? undefined,
      city: e.city ?? undefined,
      state: e.state ?? undefined,
      categoryIds,
      kind: editionKindOf(e),
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    let values: EdicaoFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // erros de validação já exibidos pelo form
    }
    const payload = toPayload(values);
    setSaving(true);
    try {
      if (editing) {
        await updateEdicao(editing.id, payload);
        message.success('Edição atualizada.');
      } else {
        await createEdicao(payload);
        message.success('Edição criada.');
      }
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      message.error(saveErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (e: EdicaoAdmin) => {
    try {
      await deleteEdicao(e.id);
      message.success('Edição excluída.');
      await load();
    } catch (err: any) {
      message.error(err?.response?.data?.msg ?? 'Falha ao excluir a edição.');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    { title: 'Nome', dataIndex: 'nome', render: (v: string | null) => v ?? '—' },
    { title: 'Subtítulo', dataIndex: 'subtitulo', render: (v: string | null) => v ?? '—' },
    {
      title: 'Tipo',
      key: 'kind',
      width: 110,
      render: (_: unknown, e: EdicaoAdmin) =>
        editionKindOf(e) === 'MIND_TRAINING' ? <Tag color="purple">Treinamento</Tag> : null,
    },
    {
      title: 'À venda',
      dataIndex: 'aVenda',
      width: 90,
      render: (v: boolean | null) =>
        v ? <Tag color="green">sim</Tag> : <Tag>não</Tag>,
    },
    { title: 'Preço', dataIndex: 'valorAVista', width: 110, render: fmtPreco },
    { title: 'Passatempos', dataIndex: 'qtdPassatempos', width: 120 },
    { title: 'Publicação', dataIndex: 'dataPublicacao', width: 120, render: fmtDate },
    {
      title: 'Ações',
      key: 'acoes',
      width: 160,
      render: (_: unknown, e: EdicaoAdmin) => (
        <Space>
          <Button size="small" onClick={() => openEdit(e)}>
            Editar
          </Button>
          <Popconfirm
            title="Excluir esta edição?"
            okText="Excluir"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDelete(e)}
          >
            <Button size="small" danger>
              Excluir
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Edições</h2>
        <Button type="primary" onClick={openCreate}>
          Nova edição
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={edicoes}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 900 }}
      />

      <Modal
        title={editing ? `Editar edição #${editing.id}` : 'Nova edição'}
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText="Salvar"
        cancelText="Cancelar"
        confirmLoading={saving}
        destroyOnClose
        width={640}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Informe o nome da edição.' }]}
          >
            <Input placeholder="Ex.: Edição de Verão" />
          </Form.Item>
          <Form.Item name="subtitulo" label="Subtítulo">
            <Input />
          </Form.Item>
          <Form.Item
            name="descricao"
            label="Descrição"
            extra={(descricaoValue?.length ?? 0) > 160 ? LEGACY_DESCRIPTION_HELP : undefined}
          >
            <TextArea rows={3} showCount maxLength={160} />
          </Form.Item>

          <Form.Item name="kind" label="Tipo">
            <Radio.Group>
              <Radio value="DEFAULT">{EDITION_KIND_LABEL.DEFAULT}</Radio>
              <Radio value="MIND_TRAINING">{EDITION_KIND_LABEL.MIND_TRAINING}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="categoryIds" label="Categorias">
            <Select
              mode="multiple"
              allowClear
              placeholder="Nenhuma"
              optionFilterProp="children"
            >
              {categories.map((c) => (
                <Select.Option key={c.id} value={c.id}>
                  {c.active ? c.name : `${c.name} (inativa)`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Space size="large" wrap>
            <Form.Item name="dataPublicacao" label="Data de publicação">
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="valorAVista" label="Preço à vista (R$)">
              <InputNumber min={0} step={0.5} precision={2} />
            </Form.Item>
            <Form.Item name="dificuldadesId" label="Dificuldade">
              <Select allowClear style={{ width: 160 }} placeholder="—">
                {dificuldades.map((d) => (
                  <Select.Option key={d.id} value={d.id}>
                    {d.nome}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Space>

          <Space size="large" wrap>
            <Form.Item name="torneioInicio" label="Início do torneio">
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="torneioFim" label="Fim do torneio">
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>
            <Form.Item name="aVenda" label="À venda" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="liberadaGratis" label="Conta na cota grátis" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Space size="large" wrap>
            <Form.Item name="author" label="Autor">
              <Input />
            </Form.Item>
            <Form.Item name="city" label="Cidade">
              <Input />
            </Form.Item>
            <Form.Item name="state" label="UF">
              <Input maxLength={2} style={{ width: 80 }} />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
};

export default EdicoesCatalog;
